import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Modal from './Modal';
import StatusPill from './StatusPill';

import {
  SortableContainer,
  SortableElement,
  SortableHandle,
  arrayMove,
 } from 'react-sortable-hoc';

import Section from './MemberApplicationQuestionPicker/Section';

const DragHandle = SortableHandle(() => <span />);

const SortableItem = SortableElement(props => {
  const dragHandle = props.sortable ? <td><DragHandle /></td> : null;
  return (
    <tr className="ui-sortable-handle">
      {dragHandle}
      <td>{props.question.label}</td>
      <td>{props.question.hint}</td>
      { !props.forceOptional
        && <td><StatusPill status={props.question.required ? 'yes' : 'no'} /></td> }
      <td>{props.types[props.question.type]}</td>
      <td className="u-text-right">
        <a
          className="Button u-mr-2"
          onClick={props.editQuestion}
        >
          <i className="Icon Icon-edit u-mr-0" />
        </a>
        <a onClick={props.deleteQuestion} className="Button Button--red">
          <i className="Icon Icon-delete u-mr-0" />
        </a>
      </td>
    </tr>
  );
}
);

const SortableList = SortableContainer(props => {
  if (props.questions.length > 0) {
    return (
      <tbody>
        {props.questions.map((question, index) => (
          <SortableItem
            key={`item-${index}`}
            index={index}
            forceOptional={props.forceOptional}
            sortable={props.sortable}
            question={question}
            types={props.types}
            editQuestion={props.editQuestion.bind(null, index)}
            deleteQuestion={props.deleteQuestion.bind(null, index)}
          />
        ))}
      </tbody>
    );
  }
  return (<tbody><tr><td colSpan="6">No questions added yet.</td></tr></tbody>);
});

export default class QuestionSchemaInput extends Component {
  static propTypes = {
    name: PropTypes.string.isRequired,
    value: PropTypes.array,
    answerTypes: PropTypes.object,
    renderSection: PropTypes.bool,
    forceOptional: PropTypes.bool,
    sortable: PropTypes.bool,
    sectionProps: PropTypes.object,
  }

  static defaultProps = {
    value: [],
    answerTypes: {},
    renderSection: false,
    sortable: false,
    forceOptional: false,
    sectionProps: {},
  }

  constructor(props) {
    super(props);
    this.formArea = React.createRef();
    this.state = {
      form: this.resetedForm(),
      editing: null,
      modalOpen: false,
      questions: this.props.value || [],
    };
  }

  resetedForm() {
    return {
      type: 'string',
      label: '',
      hint: '',
      required: !this.props.forceOptional,
      options: [''],
      range: {
        from: '',
        to: '',
      },
    };
  }

  closeModal = (e) => {
    e.preventDefault();
    const form = this.resetedForm();
    this.setState({ modalOpen: false, form });
  }

  openModal = (e) => {
    e.preventDefault();
    this.setState({ modalOpen: true });
  }

  ignoreClick(e) {
    e.preventDefault();
  }

  validateForm() {
    let specialAnswerValidation = true;

    // Validate ranges in range questions:
    // - Range minimum and maximum are defined
    // - Range maximum > minimum
    if (this.state.form.type === 'numeric_range' &&
      this.state.form.range.from >= this.state.form.range.to &&
      this.state.form.range.from === "" &&
      this.state.form.range.to === "") {
      specialAnswerValidation = false;
    }

    // Validate selection questions:
    // - There are at least 2 options
    // - Options are defined and not empty
    if ((this.state.form.type === 'multiple_select' || this.state.form.type === 'single_select') &&
      (this.state.form.options.length < 2 || this.state.form.options[0] === "" || this.state.form.options[1] === "")) {
      specialAnswerValidation = false;
    }

    return this.state.form.label !== '' && specialAnswerValidation;
  }

  createQuestion = (e) => {
    e.preventDefault();

    if (this.validateForm()) {
      const questions = this.state.questions.slice(0);

      questions.push(this.state.form);

      this.setState({ questions });
      this.closeModal(e);
    }
  }

  saveQuestion = (e) => {
    e.preventDefault();

    if (this.validateForm()) {
      const question = this.state.form;
      const questions = this.state.questions.slice(0);

      questions.splice(this.state.editing, 1, question);

      this.setState({ questions, editing: null });
      this.closeModal(e);
    }
  }

  editQuestion = (i, e) => {
    const form = this.state.questions[i];
    this.setState({ editing: i, form });
    this.openModal(e);
  }

  deleteQuestion = (i, e) => {
    e.preventDefault();
    if (confirm('Are you sure you want to delete this question?')) {
      const questions = this.state.questions;
      questions.splice(i, 1);
      this.setState({ questions });
    }
  }

  handleChange(field, accessor, e) {
    const value = e.target[accessor];
    const form = { [field]: value };
    this.setState({ form: Object.assign({}, this.state.form, form) });
  }

  handleRemoveOption(index) {
    const options = this.state.form.options.slice();
    options.splice(index, 1);
    const form = { options };
    this.setState({ form: Object.assign({}, this.state.form, form) });
  }

  handleChangeOption(index, e) {
    const value = e.target.value;
    const options = this.state.form.options.slice();
    options[index] = value;
    const form = { options };
    this.setState({ form: Object.assign({}, this.state.form, form) });
  }

  handleAddOption = () => {
    const options = this.state.form.options.slice();
    options.push('');
    const form = { options };
    this.setState({ form: Object.assign({}, this.state.form, form) });
  }

  handleRangeFrom = (e) => {
    this.handleRange('from', e.target.value);
  }

  handleRangeTo = (e) => {
    this.handleRange('to', e.target.value);
  }

  handleRange(key, value) {
    const range = { [key]: value };
    const form = { range: Object.assign({}, this.state.form.range, range) };
    this.setState({ form: Object.assign({}, this.state.form, form) });
  }

  specialAnswerType() {
    switch (this.state.form.type) {
      case 'single_select':
      case 'multiple_select':
        return (
          <div className="u-mt-4">
            <label>Options <span className="u-text-grey u-ml-2 u-text-xs">Please add at least 2 options</span></label>
            {this.state.form.options.map((option, index) =>
              <div className="u-flex">
                <div className="u-w-11/12">
                  <input
                    type="text"
                    value={option}
                    placeholder="An option"
                    onChange={this.handleChangeOption.bind(this, index)}
                  />
                </div>
                <div className="u-w-1/12">
                  <a
                    className="u-float-right inline Button Button--red"
                    onClick={this.handleRemoveOption.bind(this, index)}
                  >
                    <i className="Icon Icon-delete u-mr-0" />
                  </a>
                </div>
              </div>
            )}
            <div className="u-flex">
              <div className="u-w-full">
                <a
                  className="Button Button--primary u-float-right"
                  onClick={this.handleAddOption}
                >
                  <i className="Icon Icon-plus-square" /> Add option
                </a>
              </div>
            </div>
          </div>
        );
      case 'numeric_range':
        return (
          <div>
            <div className="u-flex u-justify-between u-mt-4">
              <div className="u-w-5/12">
                <div className="u-flex">
                  <div className="u-w-1/4">
                    <label className="inline">From:</label>
                  </div>
                  <div className="u-w-3/4">
                    <input
                      type="number"
                      value={this.state.form.range.from}
                      onChange={this.handleRangeFrom}
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
              <div className="u-w-5/12">
                <div className="u-flex">
                  <div className="u-w-1/4">
                    <label className="inline">To:</label>
                  </div>
                  <div className="u-w-3/4">
                    <input
                      type="number"
                      value={this.state.form.range.to}
                      onChange={this.handleRangeTo}
                      placeholder="10"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return '';
    }
  }

  renderForm() {
    const checkedProps = {};
    if (this.state.form.required) {
      checkedProps.defaultChecked = true;
    }

    const formAreaStyle = {
      overflowX: 'scroll',
      maxHeight: 450,
    };

    const specialAnswerType = this.specialAnswerType();
    const fieldRequired = this.props.forceOptional ? null : (
      <div className="checkbox">
        <label>
          <input
            {...checkedProps}
            type="checkbox"
            value="true"
            onClick={this.handleChange.bind(this, 'required', 'checked')}
          />
          Required
        </label>
      </div>
    );

    return (
      <Modal visible={this.state.modalOpen} onClose={this.closeModal}>
        <div className="Form">
          <div className="Form-step u-relative" style={formAreaStyle} ref={this.formArea}>
            <span className="Modal-close Modal-dismiss" onClick={this.closeModal}>
              <i className="Icon Icon-close" />
            </span>

            <h1>Add a custom question</h1>

            <label>Question</label>
            <input
              type="text"
              placeholder="Why do you like apples?"
              value={this.state.form.label}
              onChange={this.handleChange.bind(this, 'label', 'value')}
              onClick={this.handleClick}
            />
            <label>Tooltip text</label>
            <input
              type="text"
              placeholder="Tell us in 140 characters or less why apples are the best fruit!"
              value={this.state.form.hint}
              onChange={this.handleChange.bind(this, 'hint', 'value')}
            />
            {fieldRequired}
            <label>Answer type</label>
            <select
              value={this.state.form.type}
              onChange={this.handleChange.bind(this, 'type', 'value')}
            >
              {Object.keys(this.props.answerTypes).map((k) => <option key={k} value={k}>{this.props.answerTypes[k]}</option>)}
            </select>
            {specialAnswerType}
          </div>
          <div className="u-text-center u-p-8">
            <input
              type="submit"
              className="Button"
              onClick={this.state.editing === null ? this.createQuestion : this.saveQuestion}
              value="Continue"
            />
          </div>
        </div>
      </Modal>
    );
  }

  onSortEnd = ({ oldIndex, newIndex }) => {
    const questions = this.state.questions;
    this.setState({
      questions: arrayMove(questions, oldIndex, newIndex),
    });
  }

  // sortHelper is the name of the phantom row which is shown as being dragged;
  // it is created when the drag begins and destroyed when it finishes.
  // This method makes that row match the rest of its table during the drag.
  onSortStart = ({ node }) => {
    const $sortHelperCells = $('tr.draggableItem td');
    const $siblingRow = $(node);

    for (let row = 0; row < $sortHelperCells.length; row++) {
      const styles = ['width', 'padding', 'vertical-align', 'line-height', 'font-size'];

      for (let column = 0; column < styles.length; column++) {
        const matchingStyle = $($siblingRow.children('td')[row]).css(styles[column]);
        $($sortHelperCells[row]).css(styles[column], matchingStyle);
      }
    }
  }

  renderQuestionSchemaInput() {
    const form = this.renderForm();
    const sortHint = this.props.sortable ? <span>Drag questions around to reorder them.<br /></span> : null;
    let columnCount = 4;
    if (this.props.sortable) { columnCount += 1; }
    if (!this.props.forceOptional) { columnCount += 1; }
    return (
      <div className="u-mt-4">
        <table className="u-w-full List-table List-table--sortable u-select-none">
          <thead>
            <tr>
              { this.props.sortable && <th className="u-w-4" /> }
              <th>Question</th>
              <th>Tooltip Text</th>
              { !this.props.forceOptional && <th className="u-w-20">Required?</th> }
              <th className="u-w-32">Answer Type</th>
              <th className="u-w-24" />
            </tr>
          </thead>
          <SortableList
            questions={this.state.questions}
            onSortEnd={this.onSortEnd}
            onSortStart={this.onSortStart}
            useDragHandle
            forceOptional={this.props.forceOptional}
            sortable={this.props.sortable}
            types={this.props.answerTypes}
            editQuestion={this.editQuestion}
            deleteQuestion={this.deleteQuestion}
            helperClass="draggableItem"
          />
          <tfoot>
            <tr>
              <td colSpan={columnCount}>
                <span className="hint u-inline-block">
                  {sortHint}
                  Your changes will only be saved when you submit the form.
                </span>
                <button
                  className="Button u-float-right Button--primary Button--small u-mb-0"
                  onClick={this.openModal}
                >
                  <i className="Icon Icon-plus-square" /> Add question
                </button>
              </td>
            </tr>
          </tfoot>
        </table>
        {form}
        <input name={this.props.name} type="hidden" value={JSON.stringify(this.state.questions)} />
      </div>
    );
  }

  renderSection() {
    const sectionProps = Object.assign({}, this.props.sectionProps);
    sectionProps.previewButtonProps.previewData = this.state.questions;
    sectionProps.previewButtonProps.disabled = this.state.questions.length === 0;

    return (
      <Section {...sectionProps}>
        {this.renderQuestionSchemaInput()}
      </Section>
    );
  }

  render() {
    if (this.props.renderSection) {
      return this.renderSection();
    }
    return this.renderQuestionSchemaInput();
  }
}
